import React, { useMemo, useState } from 'react';
import { Button } from '@this/shared/ui/inputs/button';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import type { HotelProposalReasons } from '../../../types/hotelProposalReasons';
import {
  filterHotelItems,
  hasProposalItemHotel,
  type HotelProposalBundle,
  type HotelProposalItem,
  type HotelSegmentGroup,
  type ProposalItem
} from '../../../types/proposalBundles';
import { formatRouteDate } from '../../../utils/routeInfoFormatter';
import PartitionedSegmentBlock from './PartitionedSegmentBlock';

const groupHotelItemsBySegment = (items: ProposalItem[]): HotelSegmentGroup[] => {
  const map = new Map<number, HotelSegmentGroup>();

  items.filter(hasProposalItemHotel).forEach(item => {
    const segmentIndex = item.hotel.segment.index ?? 0;
    const existing = map.get(segmentIndex);
    if (existing) {
      existing.items.push(item);
      return;
    }

    map.set(segmentIndex, {
      index: segmentIndex,
      checkin: item.hotel.segment.checkin ?? null,
      checkout: item.hotel.segment.checkout ?? null,
      items: [item]
    });
  });

  return Array.from(map.values()).sort((left, right) => left.index - right.index);
};

const calculateHotelItemTotal = (
  item: HotelProposalItem
): { price: number | null; priceWithTax: number | null } => {
  const stayDays = item.hotel.stayDays && item.hotel.stayDays > 0 ? item.hotel.stayDays : 1;

  return {
    price: item.price !== null ? item.price * stayDays : null,
    priceWithTax: item.priceWithTax !== null ? item.priceWithTax * stayDays : null
  };
};

const sumSelectedHotelItemsTotal = (selectedItems: HotelProposalItem[]): { price: number; priceWithTax: number } =>
  selectedItems.reduce(
    (acc, item) => {
      const totals = calculateHotelItemTotal(item);
      return {
        price: acc.price + (totals.price ?? 0),
        priceWithTax: acc.priceWithTax + (totals.priceWithTax ?? 0)
      };
    },
    { price: 0, priceWithTax: 0 }
  );

const getSelectedHotelItems = (bundle: HotelProposalBundle): HotelProposalItem[] =>
  groupHotelItemsBySegment(filterHotelItems(bundle.items))
    .map(group => group.items.find(item => item.selected))
    .filter((item): item is HotelProposalItem => item != null);

const formatFullStayPeriod = (bundle: HotelProposalBundle): string | null => {
  const checkin = formatRouteDate(bundle.route.departureDate);
  const checkout = formatRouteDate(bundle.route.returnDate);
  if (!checkin || !checkout) {
    return null;
  }

  const stayDays = bundle.route.stayDays;
  const stayLabel = stayDays && stayDays > 0 ? `（${stayDays}泊${stayDays + 1}日）` : '';
  return `${checkin} チェックイン 〜 ${checkout} チェックアウト${stayLabel}`;
};

const countPartitionedSegmentProgress = (bundle: HotelProposalBundle): { selected: number; total: number } => {
  const segmentGroups = groupHotelItemsBySegment(filterHotelItems(bundle.items));
  const total = bundle.partition.segmentCount ?? segmentGroups.length;
  const selected = segmentGroups.filter(group => group.items.some(item => item.selected)).length;

  return { selected, total };
};

interface Props {
  bundle: HotelProposalBundle;
  includeTax: boolean;
  disabled: boolean;
  isSpLayout: boolean;
  onSelect: (itemId: number) => void;
  onOpenDetail: (itemId: number) => void;
  reasons: HotelProposalReasons;
}

const PartitionedHotelCard = ({
  bundle,
  includeTax,
  disabled,
  isSpLayout,
  onSelect,
  onOpenDetail,
  reasons
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(null);
  const segmentGroups = useMemo(() => groupHotelItemsBySegment(filterHotelItems(bundle.items)), [bundle.items]);
  const segmentProgress = useMemo(() => countPartitionedSegmentProgress(bundle), [bundle]);
  const fullStayPeriod = useMemo(() => formatFullStayPeriod(bundle), [bundle]);
  const selectedTotal = useMemo(() => sumSelectedHotelItemsTotal(getSelectedHotelItems(bundle)), [bundle]);
  const selectedTotalDisplay = useMemo(
    () => formatPriceWithMark(includeTax ? selectedTotal.priceWithTax : selectedTotal.price) ?? '-',
    [selectedTotal, includeTax]
  );

  return (
    <>
      {fullStayPeriod && <div style={styles.cardSub}>{fullStayPeriod}</div>}
      <div style={styles.partitionedSelectHint}>各期間から1件ずつ「選択」ボタンをクリックしてください。</div>
      <div style={styles.progressBar}>
        <div style={styles.progressTrack}>
          <div
            style={{
              ...styles.progressFill,
              width: `${(segmentProgress.selected / segmentProgress.total) * 100}%`
            }}
          />
        </div>
        <span style={styles.progressLabel}>
          {segmentProgress.selected}/{segmentProgress.total} 期間選択済
        </span>
      </div>
      {segmentGroups.length > 1 && (
        <div style={styles.periodJumpBar}>
          {segmentGroups.map(segment => (
            <Button
              key={segment.index}
              type="button"
              color={activeSegmentIndex === segment.index ? 'primary' : 'sub'}
              style={styles.periodJumpButton}
              onClick={() => {
                setActiveSegmentIndex(segment.index);
                document.getElementById(`hotel-segment-${bundle.id}-${segment.index}`)?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }}
            >
              期間{segment.index + 1}
            </Button>
          ))}
        </div>
      )}
      {segmentGroups.map(segment => (
        <PartitionedSegmentBlock
          key={segment.index}
          bundleId={bundle.id}
          segment={segment}
          includeTax={includeTax}
          disabled={disabled}
          onSelect={onSelect}
          onOpenDetail={onOpenDetail}
          isSpLayout={isSpLayout}
          reasons={reasons}
        />
      ))}
      <div style={styles.totalFooter}>
        <span style={styles.totalFooterLabel}>宿泊 合計（選択済み）</span>
        <span style={styles.totalFooterPrice}>{selectedTotalDisplay}</span>
      </div>
    </>
  );
};

export default PartitionedHotelCard;

const getStyles = (theme: AITTheme) => ({
  cardSub: {
    fontSize: '12px',
    color: '#888888',
    marginTop: '-8px',
    marginBottom: '14px',
    lineHeight: 1.5
  },
  partitionedSelectHint: {
    fontSize: '12px',
    color: '#888888',
    marginBottom: '10px',
    lineHeight: 1.5
  },
  progressBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '14px'
  },
  progressTrack: {
    flex: 1,
    height: '6px',
    backgroundColor: '#ececec',
    borderRadius: '3px',
    overflow: 'hidden' as const
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.linkColor,
    borderRadius: '3px',
    transition: 'width 0.3s'
  },
  progressLabel: {
    color: theme.linkColor,
    fontWeight: 600 as const,
    fontSize: '12px',
    whiteSpace: 'nowrap' as const
  },
  periodJumpBar: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginBottom: '12px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    backgroundColor: theme.whiteColor,
    paddingTop: '6px',
    paddingBottom: '8px',
    borderBottom: '1px solid #ececec'
  },
  periodJumpButton: {
    borderRadius: '14px',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: 600 as const,
    minWidth: 'auto'
  },
  totalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    backgroundColor: '#e6f4fb',
    borderRadius: '8px',
    marginTop: '4px',
    marginBottom: '12px'
  },
  totalFooterLabel: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor
  },
  totalFooterPrice: {
    fontSize: '18px',
    fontWeight: 700 as const,
    color: theme.linkColor
  }
});
